import React, { useState, useEffect, useRef } from 'react';
import { Utils, authJsonHeaders, formatDateTime, handleSessionExpired, getDefaultPageSize } from '../../utils';
import { FaMicrophone, FaPaperclip } from 'react-icons/fa';
import ListPaginationBar from '../common/ListPaginationBar';
import './AdminSupport.css';

const getFileUrl = (attachmentUrl) => {
  const baseUrl = Utils.baseurl.replace('/api/', '').replace(/\/$/, '');
  return baseUrl + attachmentUrl;
};

const AdminSupport = () => {
  const pageSize = getDefaultPageSize();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: pageSize, total: 0, totalPages: 0 });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams({
        page: String(pagination.page),
        limit: String(pagination.limit || pageSize),
      });
      const response = await fetch(`${Utils.baseurl}admin/support/conversations?${qs}`, {
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        setConversations(result.data || []);
        if (result.pagination) {
          setPagination((p) => ({ ...p, ...result.pagination }));
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(Utils.baseurl + 'admin/support/messages', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({ conversationId }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        setMessages(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    await fetchMessages(conversation.id);
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;

    try {
      const response = await fetch(Utils.baseurl + 'admin/support/reply', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          message: replyMessage,
        }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        setReplyMessage('');
        await fetchMessages(selectedConversation.id);
        await fetchConversations();
      }
    } catch (error) {
      console.error('Error sending reply:', error);
    }
  };

  const closeConversation = async () => {
    if (!selectedConversation) return;

    try {
      const response = await fetch(Utils.baseurl + 'admin/support/close', {
        method: 'POST',
        headers: authJsonHeaders(Utils.storageKeys.adminToken),
        body: JSON.stringify({ conversationId: selectedConversation.id }),
      });
      const result = await response.json();
      if (handleSessionExpired(response, result)) return;
      if (result.status) {
        await fetchConversations();
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error closing conversation:', error);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: '#22c55e',
      pending: '#f59e0b',
      closed: '#6b7280'
    };
    return (
      <span className="status-badge" style={{ backgroundColor: colors[status] }}>
        {status}
      </span>
    );
  };

  if (loading && conversations.length === 0) {
    return <div className="admin-support-loading">Loading...</div>;
  }

  return (
    <div className="admin-support">
      <div className="admin-support-header">
        <h1>Support Messages</h1>
        <p>Manage customer support conversations</p>
      </div>

      <div className="admin-support-container">
        {/* Conversations List */}
        <div className="conversations-panel">
          <div className="panel-header">
            <h3>Conversations ({pagination.total || conversations.length})</h3>
          </div>
          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="no-conversations">No conversations yet</div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''}`}
                  onClick={() => selectConversation(conv)}
                >
                  <div className="conv-header">
                    <span className="conv-user">
                      {conv.user?.first_name || conv.user?.email || 'User #' + conv.userId}
                    </span>
                    {getStatusBadge(conv.status)}
                  </div>
                  <p className="conv-preview">{conv.last_message || 'No messages'}</p>
                  <span className="conv-date">{formatDateTime(conv.updatedAt)}</span>
                  {conv.unread_count > 0 && (
                    <span className="unread-indicator">{conv.unread_count} new</span>
                  )}
                </div>
              ))
            )}
          </div>
          <ListPaginationBar
            page={pagination.page}
            setPage={(n) => setPagination((p) => ({ ...p, page: n }))}
            total={pagination.total}
            pageSize={pagination.limit}
            totalPages={pagination.totalPages}
            ariaLabel="Admin support conversations pagination"
            className="px-2 py-2"
            loading={loading}
          />
        </div>

        {/* Messages Panel */}
        <div className="messages-panel">
          {selectedConversation ? (
            <>
              <div className="panel-header">
                <div className="header-info">
                  <h3>
                    {selectedConversation.user?.first_name || selectedConversation.user?.email || 'User'}
                  </h3>
                  <span className="header-email">{selectedConversation.user?.email}</span>
                </div>
                <div className="header-actions">
                  {selectedConversation.status !== 'closed' && (
                    <button className="close-conv-btn" onClick={closeConversation}>
                      Close Conversation
                    </button>
                  )}
                </div>
              </div>

              <div className="messages-container">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.sender_type === 'admin' ? 'admin' : 'user'}`}
                  >
                    <div className="message-content">
                      {msg.attachment_url ? (
                        <div className="message-attachment">
                          {msg.attachment_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                            <a href={getFileUrl(msg.attachment_url)} target="_blank" rel="noopener noreferrer">
                              <img
                                src={getFileUrl(msg.attachment_url)}
                                alt={msg.attachment_name || 'Attachment'}
                                className="attachment-image"
                              />
                            </a>
                          ) : msg.attachment_url.match(/\.(webm|mp3|wav)$/i) ? (
                            <div className="voice-message">
                              <span><FaMicrophone style={{ marginRight: '6px' }} />Voice Message</span>
                              <audio controls src={getFileUrl(msg.attachment_url)} />
                            </div>
                          ) : (
                            <a
                              href={getFileUrl(msg.attachment_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="file-attachment"
                            >
                              <FaPaperclip style={{ marginRight: '6px' }} />
                              {msg.attachment_name || 'Download File'}
                            </a>
                          )}
                        </div>
                      ) : (
                        <p>{msg.message}</p>
                      )}
                      <span className="message-time">{formatDateTime(msg.createdAt)}</span>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {selectedConversation.status !== 'closed' && (
                <div className="reply-container">
                  <textarea
                    placeholder="Type your reply..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                  />
                  <button onClick={sendReply} disabled={!replyMessage.trim()}>
                    Send Reply
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <p>Select a conversation to view messages</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupport;
